import React, { useState } from "react";
import { useSiteConfig } from "../../context/SiteConfigContext";
import contactAPI from "../../api/contactApi";
import { showSuccess, showError } from "../../utils/toastUtils";
import useAlphabetsInput from "../../hooks/useAlphabeticInput";
import useNumericInput from "../../hooks/useNumericInput";
import Breadcrump from "../../components/Breadcrump";
import { Mail, Phone, MapPin, Clock, MessageSquare, Send, Sparkles } from "lucide-react";
import {
  FaFacebook,
  FaInstagram,
  FaTwitter,
  FaYoutube,
  FaLinkedin,
} from "react-icons/fa";

function ContactUs() {
  useAlphabetsInput();
  useNumericInput();
  const { config } = useSiteConfig();

  const INITAL_FORM_DATA = {
    email: "",
    phone: "",
    name: "",
    message: "",
  };

  const [formData, setFormData] = useState(INITAL_FORM_DATA);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isValidEmail = emailRegex.test(formData.email);

  const validate = () => {
    const newErrors = {};
    if (!formData.email) {
      newErrors.email = "Please enter email address";
    } else if (!isValidEmail) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.phone) {
      newErrors.phone = "Please enter phone";
    }

    if (!formData.name) {
      newErrors.name = "Please enter name";
    }

    if (!formData.message) {
      newErrors.message = "Please enter message";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await contactAPI.store(formData);
      if (res.code === 200) {
        showSuccess(res.message);
        setFormData(INITAL_FORM_DATA);
      } else {
        showError(res.message);
      }
    } catch (err) {
      showError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Breadcrump title="Contact Us" />
      <div className="min-h-screen bg-[#FFFDF8] text-[#2E211A] relative overflow-hidden">
        {/* Decorative Background Botanical Glow Elements */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[#F6EFE2] rounded-full blur-3xl pointer-events-none opacity-60" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-12 relative z-10">

          {/* Header Intro */}
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-[999px] bg-[#F6EFE2] text-[#A6472E] text-xs font-bold uppercase tracking-widest border border-[#E5D9C3] shadow-[0_1px_3px_rgba(74,47,30,0.08)] mb-4">
              <Sparkles size={14} className="animate-pulse" /> Connect With Annvanta
            </div>
            <h1 className="text-2xl md:text-5xl font-bold text-[#4B3226] font-['Bebas_Neue'] tracking-wider leading-none mb-4">
              Let’s Create Something Beautiful Together
            </h1>
            <p className="text-base md:text-md text-[#6B5D53] font-normal leading-relaxed">
              Have a question about our collections or need personalized assistance? Explore our info channels below or send us a direct message.
            </p>
          </div>

          {/* TOP SECTION: Horizontal Interactive Info Cards Grid (No more strict Left/Right layout) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">

            {/* Card 1: Support Hours */}
            <div className="bg-[#FFFFFF] rounded-[20px] border border-[#E5D9C3] p-6 shadow-[0_4px_20px_rgba(74,47,30,0.06)] hover:-translate-y-1 transition-all duration-300 group">
              <div className="w-12 h-12 rounded-[14px] bg-[#F6EFE2] text-[#A6472E] flex items-center justify-center mb-4 group-hover:bg-[#A6472E] group-hover:text-white transition-colors duration-300">
                <Clock size={22} />
              </div>
              <h4 className="text-xs font-bold tracking-widest text-[#6B5D53] uppercase mb-1">Support Hours</h4>
              <p className="text-sm font-bold text-[#4B3226]">Mon - Sun</p>
              <p className="text-xs text-[#6B5D53] mt-0.5">9:00 am - 6:00 pm</p>
            </div>

            {/* Card 2: Direct Phone & WhatsApp */}
            <div className="bg-[#FFFFFF] rounded-[20px] border border-[#E5D9C3] p-6 shadow-[0_4px_20px_rgba(74,47,30,0.06)] hover:-translate-y-1 transition-all duration-300 group">
              <div className="w-12 h-12 rounded-[14px] bg-[#F6EFE2] text-[#A6472E] flex items-center justify-center mb-4 group-hover:bg-[#A6472E] group-hover:text-white transition-colors duration-300">
                <Phone size={22} />
              </div>
              <h4 className="text-xs font-bold tracking-widest text-[#6B5D53] uppercase mb-1">Direct Lines</h4>
              <a href={`tel:+91 ${config?.phone}`} className="text-sm font-bold text-[#4B3226] hover:text-[#A6472E] block transition-colors">
                +91 {config?.phone}
              </a>
              <a href={`https://wa.me/${config?.whatsappNumber}`} target="_blank" rel="noreferrer" className="text-xs text-[#A6472E] font-medium mt-0.5 inline-block hover:underline">
                WhatsApp Chat Available →
              </a>
            </div>

            {/* Card 3: Email Inquiry */}
            <div className="bg-[#FFFFFF] rounded-[20px] border border-[#E5D9C3] p-6 shadow-[0_4px_20px_rgba(74,47,30,0.06)] hover:-translate-y-1 transition-all duration-300 group">
              <div className="w-12 h-12 rounded-[14px] bg-[#F6EFE2] text-[#A6472E] flex items-center justify-center mb-4 group-hover:bg-[#A6472E] group-hover:text-white transition-colors duration-300">
                <Mail size={22} />
              </div>
              <h4 className="text-xs font-bold tracking-widest text-[#6B5D53] uppercase mb-1">Email Us</h4>
              <a href={`mailto:${config?.email}`} className="text-sm font-bold text-[#4B3226] hover:text-[#A6472E] block transition-colors break-all">
                {config?.email}
              </a>
              <p className="text-xs text-[#6B5D53] mt-0.5">Quick email responses</p>
            </div>

            {/* Card 4: Location */}
            <div className="bg-[#FFFFFF] rounded-[20px] border border-[#E5D9C3] p-6 shadow-[0_4px_20px_rgba(74,47,30,0.06)] hover:-translate-y-1 transition-all duration-300 group">
              <div className="w-12 h-12 rounded-[14px] bg-[#F6EFE2] text-[#A6472E] flex items-center justify-center mb-4 group-hover:bg-[#A6472E] group-hover:text-white transition-colors duration-300">
                <MapPin size={22} />
              </div>
              
            <h4 className="text-xs font-bold tracking-widest text-[#6B5D53] uppercase mb-1">Our Location</h4>
               <p className="mt-2 text-[0.6rem] leading-[1.7] tracking-[0.12em] text-[#4B3226] [font-family:var(--font-subheading)]">
                {config?.address?.addressLine}
              </p>
              <p className="text-sm font-bold text-[#4B3226] line-clamp-2">
                {[config?.address?.district, config?.address?.state].filter(Boolean).join(", ")}
                {config?.address?.pincode ? ` - ${config?.address.pincode}` : ""}
              </p>
              
            </div>

          </div>

          {/* CENTERED STACKED UNIQUE SECTION: Elegant Clean Form Box with Social Header */}
          <div className="max-w-4xl mx-auto bg-[#FFFFFF] rounded-[28px] border border-[#E5D9C3] shadow-[0_20px_50px_rgba(74,47,30,0.1)] p-8 md:p-14 relative overflow-hidden">

            <div className="text-center max-w-xl mx-auto mb-10 pb-8 border-b border-[#E5D9C3]">
              <span className="text-xs font-bold tracking-widest text-[#A6472E] uppercase block mb-1">Drop Us A Line</span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#4B3226] font-['Bebas_Neue'] tracking-wide">
                Send Your Message
              </h2>
              <p className="text-xs md:text-sm text-[#6B5D53] mt-2">
                Fill out the inquiry form below and our customer support team will get back to you promptly.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Name */}
                <div className="space-y-2">
                  <label htmlFor="name" className="block text-xs font-bold tracking-wider text-[#6B5D53] uppercase">
                    Your Full Name <span className="text-[#A6472E]">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    className="w-full px-4 py-3.5 bg-[#FFFDF8] border border-[#E5D9C3] rounded-[12px] text-[#2E211A] text-sm focus:outline-none focus:border-[#A6472E] focus:ring-2 focus:ring-[#A6472E]/20 transition-all only-alphabets shadow-[0_1px_2px_rgba(74,47,30,0.04)]"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="e.g. Aarav Sharma"
                    required
                  />
                  {errors.name && (
                    <span className="text-xs text-[#A6472E] font-medium block">{errors.name}</span>
                  )}
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <label htmlFor="email" className="block text-xs font-bold tracking-wider text-[#6B5D53] uppercase">
                    Email Address <span className="text-[#A6472E]">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className="w-full px-4 py-3.5 bg-[#FFFDF8] border border-[#E5D9C3] rounded-[12px] text-[#2E211A] text-sm focus:outline-none focus:border-[#A6472E] focus:ring-2 focus:ring-[#A6472E]/20 transition-all shadow-[0_1px_2px_rgba(74,47,30,0.04)]"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="e.g. aarav@example.com"
                    required
                  />
                  {errors.email && (
                    <span className="text-xs text-[#A6472E] font-medium block">{errors.email}</span>
                  )}
                </div>
              </div>

              {/* Phone */}
              <div className="space-y-2">
                <label htmlFor="phone" className="block text-xs font-bold tracking-wider text-[#6B5D53] uppercase">
                  Phone Number <span className="text-[#A6472E]">*</span>
                </label>
                <input
                  type="text"
                  id="phone"
                  maxLength={10}
                  name="phone"
                  className="w-full px-4 py-3.5 bg-[#FFFDF8] border border-[#E5D9C3] rounded-[12px] text-[#2E211A] text-sm focus:outline-none focus:border-[#A6472E] focus:ring-2 focus:ring-[#A6472E]/20 transition-all only-numeric shadow-[0_1px_2px_rgba(74,47,30,0.04)]"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Enter 10-digit mobile number"
                  required
                />
                {errors.phone && (
                  <span className="text-xs text-[#A6472E] font-medium block">{errors.phone}</span>
                )}
              </div>

              {/* Message */}
              <div className="space-y-2">
                <label htmlFor="message" className="block text-xs font-bold tracking-wider text-[#6B5D53] uppercase">
                  Your Message <span className="text-[#A6472E]">*</span>
                </label>
                <textarea
                  id="message"
                  rows={5}
                  name="message"
                  className="w-full px-4 py-3.5 bg-[#FFFDF8] border border-[#E5D9C3] rounded-[12px] text-[#2E211A] text-sm focus:outline-none focus:border-[#A6472E] focus:ring-2 focus:ring-[#A6472E]/20 transition-all resize-y shadow-[0_1px_2px_rgba(74,47,30,0.04)]"
                  placeholder="Tell us more about your requirements or queries..."
                  value={formData.message}
                  onChange={handleChange}
                  required
                />
                {errors.message && (
                  <span className="text-xs text-[#A6472E] font-medium block">{errors.message}</span>
                )}
              </div>

              {/* Submit Button */}
              <div className="pt-2 text-center">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="  custom-btn-contact w-full sm:w-auto inline-flex items-center justify-center  gap-0 lg:gap-2  xl:gap-2 px-8 py-3 bg-[#A6472E] text-white text-sm font-bold uppercase tracking-wider rounded-[12px] shadow-[0_4px_16px_rgba(166,71,46,0.3)] hover:bg-[#7A2F1E] active:scale-[0.99] transition-all duration-200 disabled:opacity-70"
                >
                  {isSubmitting ? "Sending Message..." : <>Send Message <Send size={16} /></>}
                </button>
              </div>
            </form>

            {/* Social Links Footer inside Form Card */}
            <div className="mt-12 pt-8 border-t border-[#E5D9C3] flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
              <div>
                <h5 className="text-xs font-bold uppercase tracking-wider text-[#4B3226]">Follow Our Social Networks</h5>
                <p className="text-xs text-[#6B5D53] mt-0.5">Stay updated with our latest design releases and stories.</p>
              </div>

              <ul className="flex items-center gap-2.5 m-0 p-0 list-none">
                {config?.socialMedia?.facebook && (
                  <li>
                    <a href={config?.socialMedia?.facebook} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-[10px] bg-[#F6EFE2] flex items-center justify-center text-[#4B3226] hover:bg-[#A6472E] hover:text-white transition-all duration-200 shadow-sm" aria-label="Facebook">
                      <FaFacebook size={15} />
                    </a>
                  </li>
                )}
                {config?.socialMedia?.instagram && (
                  <li>
                    <a href={config?.socialMedia?.instagram} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-[10px] bg-[#F6EFE2] flex items-center justify-center text-[#4B3226] hover:bg-[#A6472E] hover:text-white transition-all duration-200 shadow-sm" aria-label="Instagram">
                      <FaInstagram size={15} />
                    </a>
                  </li>
                )}
                {config?.socialMedia?.twitter && (
                  <li>
                    <a href={config?.socialMedia?.twitter} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-[10px] bg-[#F6EFE2] flex items-center justify-center text-[#4B3226] hover:bg-[#A6472E] hover:text-white transition-all duration-200 shadow-sm" aria-label="Twitter">
                      <FaTwitter size={15} />
                    </a>
                  </li>
                )}
                {config?.socialMedia?.youtube && (
                  <li>
                    <a href={config?.socialMedia?.youtube} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-[10px] bg-[#F6EFE2] flex items-center justify-center text-[#4B3226] hover:bg-[#A6472E] hover:text-white transition-all duration-200 shadow-sm" aria-label="YouTube">
                      <FaYoutube size={15} />
                    </a>
                  </li>
                )}
                {config?.socialMedia?.linkedin && (
                  <li>
                    <a href={config?.socialMedia?.linkedin} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-[10px] bg-[#F6EFE2] flex items-center justify-center text-[#4B3226] hover:bg-[#A6472E] hover:text-white transition-all duration-200 shadow-sm" aria-label="LinkedIn">
                      <FaLinkedin size={15} />
                    </a>
                  </li>
                )}
              </ul>
            </div>
          </div>

          {/* Map Section */}
          {config?.address?.mapLocation && (
            <div className="mt-16 max-w-4xl mx-auto rounded-[28px] overflow-hidden border border-[#E5D9C3] shadow-[0_10px_30px_rgba(74,47,30,0.08)] bg-[#FFFFFF] h-[400px]">
              <iframe
                src={config?.address?.mapLocation}
                title="Store Location Map"
                className="w-full h-full filter contrast-[95%]"
                style={{ border: "none" }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default ContactUs;